import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Typography, Box, Paper, Divider, Select, MenuItem, FormControl,
    InputLabel, TextField, Button, Stack, Grid, Switch, FormControlLabel, FormGroup,
    TextareaAutosize
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { styled } from '@mui/material/styles';
import { updateConfigAlarma } from '../../../../../actions/alarmaActions';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { useTheme } from '@emotion/react';

// --- Funciones de Lógica de Bits ---
function buildUint16FromBits(bitConfig) {
    let resultUint16 = 0;
    for (const bitName in bitConfig) {
        if (Object.hasOwnProperty.call(bitConfig, bitName)) {
            const bitInfo = bitConfig[bitName];
            if (bitInfo.value === 1) {
                resultUint16 |= (1 << bitInfo.position);
            }
        }
    }
    return resultUint16;
}

const bitDefinitionsTemplate = {
    "habilitacion": { label: "Habilitación", position: 0, value: 0 },
    "notificarArmada": { label: "Notificar Armada", position: 1, value: 0 },
    "notificarDesarmada": { label: "Notificar Desarmada", position: 2, value: 0 },
    "notificarAlarmada": { label: "Notificar Alarmada", position: 3, value: 0 },
    "notificarDlyIngreso": { label: "Notificar Delay Ingreso", position: 4, value: 0 },
    "notificarBateria": { label: "Notificar Batería", position: 5, value: 0 },
    "permitirArmar": { label: "Permitir Armar", position: 6, value: 0 },
    "permitirDesarmar": { label: "Permitir Desarmar", position: 7, value: 0 },
    "permitirConsulta": { label: "Permitir Consulta", position: 8, value: 0 },
    "permitirControlPerfericos": { label: "Permitir Ctrl Periféricos", position: 9, value: 0 },
    "permitirConfigurar": { label: "Permitir Configurar", position: 10, value: 0 },
};

// --- Generador de Estado Inicial (para el padre) ---
export const generarEstadoInicialSms = () => ({
    intervaloIni: 60,
    intervaloInc: 60,
    habConPass: 0,
    numCelulares: 4,
    celulares: Array.from({ length: 4 }, (_, i) => ({
        id: i,
        numero: "",
        hab: 0,
    })),
});

// --- Componentes de Estilo ---
const StyledTextarea = styled(TextareaAutosize)(/* ... se mantiene igual ... */);

// --- NUEVA FUNCIÓN DE INICIALIZACIÓN ---
const initializeDataForView = (config) => {
    const initialCellphones = {};
    config.celulares.forEach((cel, index) => {
        const phoneBitConfig = {};
        for (const bitKey in bitDefinitionsTemplate) {
            const bitValue = (cel.hab >> bitDefinitionsTemplate[bitKey].position) & 1;
            phoneBitConfig[bitKey] = { ...bitDefinitionsTemplate[bitKey], value: bitValue };
        }
        initialCellphones[index] = { id: index, numero: cel.numero, bitConfig: phoneBitConfig };
    });

    return {
        global: {
            intervaloIni: config.intervaloIni,
            intervaloInc: config.intervaloInc,
            habConPass: config.habConPass,
        },
        cellphones: initialCellphones,
    };
};

//************************************************************************************************************************** */
// --- El Componente del Formulario ---
function ConfiguracionSmsForm({ config, setConfig, state, setState, originalConfigRef}) {
    const { jsonOutput, formDirty, jsonNeedsUpdate, selectedCellphone = 0 } = state;
    const theme = useTheme();
    
    // Valores guardados en nuestro appStore y/o context
    const { currentUser, device, startLoading, endLoading } = useAppStore(appStore => appStore);
    const { dispatch } = useValue();

    // Inicializamos el estado de la vista directamente desde las props
    const [viewState, setViewState] = useState(() => initializeDataForView(config));

    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current) {
            const newConfig = {
                intervaloIni: viewState.global.intervaloIni,
                intervaloInc: viewState.global.intervaloInc,
                habConPass: viewState.global.habConPass,
                numCelulares: Object.keys(viewState.cellphones).length,
                celulares: Object.values(viewState.cellphones).map(phoneData => ({
                    id: phoneData.id,
                    numero: phoneData.numero || "",
                    hab: buildUint16FromBits(phoneData.bitConfig),
                })),
            };
            setConfig(newConfig);
        } else {
            isMounted.current = true;
        }
    }, [viewState, setConfig]);


    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = currentConfigString !== originalConfigString;

        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '{}');
            needsUpdate = JSON.stringify(jsonObject) !== JSON.stringify(config);
        } catch (e) { needsUpdate = true; }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({ ...prev, formDirty: isDirty, jsonNeedsUpdate: needsUpdate }));
        }
    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);


    // --- MANEJADORES ---
    const handleCellphoneChange = (event) => {
        setState(prev => ({ ...prev, selectedCellphone: Number(event.target.value) }));
    };

    const handleGlobalChange = (event) => {
        const { name, value, type, checked } = event.target;
        const finalValue = type === 'checkbox' ? (checked ? 1 : 0) : parseInt(value, 10) || 0;
        setViewState(prev => ({
            ...prev,
            global: { ...prev.global, [name]: finalValue }
        }));
    };

    const handlePhoneDataChange = (field, value) => {
        setViewState(prev => ({
            ...prev,
            cellphones: {
                ...prev.cellphones,
                [selectedCellphone]: {
                    ...prev.cellphones[selectedCellphone],
                    [field]: value,
                }
            }
        }));
    };

    const handleBitChange = (bitName, checked) => {
        const currentPhoneConfig = viewState.cellphones[selectedCellphone];
        const updatedBitConfig = { ...currentPhoneConfig.bitConfig };
        updatedBitConfig[bitName] = { ...updatedBitConfig[bitName], value: checked ? 1 : 0 };
        handlePhoneDataChange('bitConfig', updatedBitConfig);
    };

    const handleGenerateJson = () => {
        setState(prev => ({ ...prev, jsonOutput: JSON.stringify(config, null, 2), jsonNeedsUpdate: false }));
    };

    const sendConfig = async () => {
        //console.log("--- Enviando Configuración JSON ---");
        startLoading();
        setState(prev => ({ ...prev, configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black"}));
        
        const updateResult = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_SMS", config, dispatch);
        //console.log("updateResult: ", updateResult);

        if (updateResult?.deviceResponse === 0) {
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false, configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green"}));
            alert(`Configuración enviada con éxito (ID: ${updateResult.insertId})`);
        } else {
            setState(prev => ({ ...prev, configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red"}));
            alert("Error al enviar la configuración.");
        }
        endLoading();
    };

    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            const configOriginal = originalConfigRef.current;
            setConfig(configOriginal);
            setViewState(initializeDataForView(configOriginal));
        }
    };

    const currentCellphoneData = viewState.cellphones[selectedCellphone] || { numero: '', bitConfig: {} };

    // --- RENDERIZADO ---
    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración SMS</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={3}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>Parámetros Globales</Typography>
                                    <TextField fullWidth sx={{ mb: 2 }} name="intervaloIni" label="Intervalo Inicial (0-900)" type="number" value={viewState.global.intervaloIni} onChange={handleGlobalChange} />
                                    <TextField fullWidth sx={{ mb: 2 }} name="intervaloInc" label="Intervalo Incremento (0-900)" type="number" value={viewState.global.intervaloInc} onChange={handleGlobalChange} />
                                    <FormControlLabel control={<Switch name="habConPass" checked={!!viewState.global.habConPass} onChange={handleGlobalChange} />} label="Habilitar Contraseña" />
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>Celulares / SMS</Typography>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Seleccionar Celular</InputLabel>
                                        <Select value={selectedCellphone} label="Seleccionar Celular" onChange={handleCellphoneChange}>
                                            {Object.keys(viewState.cellphones).map(key => <MenuItem key={key} value={Number(key)}>Celular {key}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <MuiTelInput label="Número de Celular" defaultCountry="BO" value={currentCellphoneData.numero} onChange={(newVal) => handlePhoneDataChange('numero', newVal)} fullWidth sx={{ mb: 2 }} />
                                    <FormGroup>
                                        {Object.keys(currentCellphoneData.bitConfig).map(bitName => (
                                            <FormControlLabel key={bitName} control={
                                                <Switch checked={!!currentCellphoneData.bitConfig[bitName].value} onChange={(e) => handleBitChange(bitName, e.target.checked)} />
                                            } label={currentCellphoneData.bitConfig[bitName].label} />
                                        ))}
                                    </FormGroup>
                                </Paper>
                            </Stack>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        position: 'absolute', top: 0, left: 12,
                                        transform: 'translateY(-50%)', bgcolor: 'background.paper',
                                        px: 1, color: 'primary.main', fontWeight: 'bold',
                                        fontSize: '0.8rem', textTransform: 'uppercase',
                                    }}
                                >
                                    JSON de Configuración
                                </Typography>
                                {jsonOutput ? (
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                        <TextareaAutosize
                                            value={jsonOutput}
                                            minRows={10}
                                            maxRows={40}
                                            style={{
                                                width: '100%',
                                                border: `1px solid ${jsonNeedsUpdate ? theme.palette.warning.main : theme.palette.divider}`,
                                                borderRadius: '4px',
                                                padding: '8px',
                                                fontFamily: 'monospace',
                                                resize: 'vertical',
                                                backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : theme.palette.background.paper,
                                                color: theme.palette.text.primary,
                                            }}
                                            readOnly
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography color="text.secondary">
                                            Genera el JSON para previsualizarlo aquí.
                                        </Typography>
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    color={jsonNeedsUpdate ? "warning" : "primary"}
                                    onClick={handleGenerateJson}
                                    sx={{ mt: 'auto' }}
                                >
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>RESUMEN DE CONFIGURACIÓN</Typography>
                                <Typography variant="body2">**Celular Seleccionado:** Celular {selectedCellphone}</Typography>
                                <Typography variant="body2">**Número:** {currentCellphoneData.numero || 'N/A'}</Typography>
                                <Typography variant="body2">**Valor Habilitación (uint16):** 0x{buildUint16FromBits(currentCellphoneData.bitConfig).toString(16).toUpperCase()}</Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}>**Formulario Modificado:** {formDirty ? 'Sí' : 'No'}</Typography>
                                    <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}>**JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Fila inferior: Resultados del envio de la configuracion al dispositivo */}
                        <Grid item xs={12} md={12}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 12,
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'background.paper',
                                        px: 1,
                                        color: 'primary.main',
                                        fontWeight: 'bold',
                                        fontSize: '0.8rem',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Resultado envío al Dispositivo
                                </Typography>

                                <Box>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                    <strong>ConfigId: </strong>{state.configId}
                                    </Typography>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: state.color }}>
                                        <strong>Estado: </strong>{state.estado}
                                    </Typography>
                                </Box>

                            </Paper>
                        </Grid>

                        {/* Botones de Acción */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={jsonNeedsUpdate}>Enviar</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionSmsForm;